import api from './api';

export interface MediaFile {
  id: number;
  original_name: string;
  filename: string;
  path: string;
  url?: string;
  mime_type: string;
  file_type: 'image' | 'document' | 'video' | 'audio' | 'other';
  file_size: number;
  dimensions?: {
    width: number;
    height: number;
  } | null;
  alt_text?: string;
  title: string;
  description?: string;
  uploaded_by: number;
  upload_date: string;
  created_at: string;
  updated_at: string;
  uploader?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface MediaListResponse {
  success: boolean;
  data: {
    current_page: number;
    data: MediaFile[];
    first_page_url: string;
    from: number;
    last_page: number;
    last_page_url: string;
    links: Array<{
      url?: string;
      label: string;
      active: boolean;
    }>;
    next_page_url?: string;
    path: string;
    per_page: number;
    prev_page_url?: string;
    to: number;
    total: number;
  };
}

export interface MediaListParams {
  search?: string;
  file_type?: string;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface MediaFilterOptionsResponse {
  success: boolean;
  data: {
    file_types: FilterOption[];
  };
}

export interface MediaUploadData {
  file: File;
  title?: string;
  alt_text?: string;
  description?: string;
}

export interface MediaUpdateData {
  title?: string;
  alt_text?: string;
  description?: string;
}

export interface MediaStats {
  success: boolean;
  data: {
    total_files: number;
    total_size: number;
    formatted_total_size: string;
    type_stats: Array<{
      file_type: string;
      count: number;
      size: number;
    }>;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  errors?: Record<string, string[]>;
}

class MediaService {
  // 获取媒体列表
  async getMediaList(params: MediaListParams = {}): Promise<MediaListResponse> {
    const response = await api.get('/media', { params });
    return response.data;
  }

  // 获取筛选选项
  async getFilterOptions(): Promise<MediaFilterOptionsResponse> {
    const response = await api.get('/media/filter-options');
    return response.data;
  }

  // 上传媒体文件
  async uploadMedia(data: MediaUploadData): Promise<ApiResponse<MediaFile>> {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.title) {
      formData.append('title', data.title);
    }
    if (data.alt_text) {
      formData.append('alt_text', data.alt_text);
    }
    if (data.description) {
      formData.append('description', data.description);
    }

    const response = await api.post('/media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // 获取单个媒体文件信息
  async getMedia(id: number): Promise<ApiResponse<MediaFile>> {
    const response = await api.get(`/media/${id}`);
    return response.data;
  }

  // 更新媒体信息
  async updateMedia(id: number, data: MediaUpdateData): Promise<ApiResponse<MediaFile>> {
    const response = await api.put(`/media/${id}`, data);
    return response.data;
  }

  // 删除媒体文件
  async deleteMedia(id: number): Promise<ApiResponse<void>> {
    const response = await api.delete(`/media/${id}`);
    return response.data;
  }

  // 批量删除媒体文件
  async batchDelete(ids: number[]): Promise<ApiResponse<void>> {
    const response = await api.post('/media/batch-delete', { ids });
    return response.data;
  }

  // 下载媒体文件
  async downloadMedia(id: number, fallbackFilename?: string): Promise<void> {
    try {
      // 1. 获取签名下载链接
      const response = await api.get(`/media/${id}/download-url`);
      
      if (response.data?.success && response.data?.data?.download_url) {
        const url = response.data.data.download_url;
        
        // 2. 创建临时链接并点击
        const link = document.createElement('a');
        link.href = url;
        // 注意：跨域或非直接同源时 download 属性可能无效，但后端已设置 Content-Disposition: attachment
        if (fallbackFilename) {
          link.setAttribute('download', fallbackFilename);
        }
        link.target = '_blank'; // 在新标签页打开以确保兼容性，避免影响当前 SPA 状态
        link.rel = 'noopener noreferrer';
        
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        throw new Error(response.data?.message || '获取下载链接失败');
      }
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

  // 获取统计信息
  async getStats(): Promise<MediaStats> {
    const response = await api.get('/media-stats');
    return response.data;
  }

  // 格式化文件大小
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // 检查文件是否为图片
  isImage(mediaFile: MediaFile): boolean {
    return mediaFile.file_type === 'image';
  }

  // 根据扩展名/类型返回细分的文件类型关键词（用于选择图标）
  getFileTypeIcon(mediaFile: MediaFile): string {
    const extFromName = (name?: string): string => {
      if (!name) return '';
      const parts = name.split('.');
      return parts.length > 1 ? parts.pop()!.toLowerCase() : '';
    };

    const ext = extFromName(mediaFile.original_name) || extFromName(mediaFile.filename) || extFromName(mediaFile.path);
    const mime = (mediaFile.mime_type || '').toLowerCase();

    const is = (list: string[]) => list.includes(ext) || list.some(m => mime.includes(m));

    if (is(['doc', 'docx', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'])) {
      return 'word';
    }
    if (is(['xls', 'xlsx', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'])) {
      return 'excel';
    }
    if (is(['ppt', 'pptx', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'])) {
      return 'ppt';
    }
    if (is(['pdf', 'application/pdf'])) {
      return 'pdf';
    }
    if (is(['zip', 'application/zip'])) {
      return 'zip';
    }
    if (is(['rar', 'application/x-rar-compressed'])) {
      return 'rar';
    }
    if (mediaFile.file_type === 'image') return 'image';
    if (mediaFile.file_type === 'video') return 'video';
    if (mediaFile.file_type === 'audio') return 'audio';
    if (mediaFile.file_type === 'document') return 'document';

    return 'other';
  }

  getMediaUrl(media: MediaFile): string {
    if (media.url) {
      return media.url;
    }
    const baseUrl = process.env.REACT_APP_STORAGE_URL || 'http://trademark.test/storage';
    return `${baseUrl}/${media.path}`;
  }
}
export default new MediaService();
